import { useCallback, useMemo } from 'react';

import qs from 'qs';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePsfSharePageData, usePsfSharePageState } from '#src/app/pages/psf/share/pageContext';
import type { PromoteSlideUpProps } from '#src/app/pages/psf/share/shared/components';
import { usePageCopy } from '#src/app/pages/psf/share/shared/hooks';
import { useQueryParamValue } from '#src/app/shared/hooks/location';

import { usePromoteSlideUp } from './hooks/usePromoteSlideUp';

type PromoteSlideUpState = {
	isEnabled: boolean;
	isOpen: boolean;
	isPending: boolean;
	amountLocalized: string;
};

type Result = ModelHookResult<
	{
		skipLabel: string;
		skipUrl: string;
		isFooterTapHandledInPage: boolean;
		promoteSlideUp: PromoteSlideUpState;
	},
	{
		handleSkipClick: () => void;
		handlePromoteSlideUpOpenChange: PromoteSlideUpProps['onOpenChange'];
		handlePromoteSlideUpChipIn: PromoteSlideUpProps['onChipIn'];
		handlePromoteSlideUpSkip: PromoteSlideUpProps['onSkip'];
	}
>;

export function usePageFooter(): Result {
	const sourceLocation = useQueryParamValue('source_location');
	const guest = useQueryParamValue('guest');
	const fromGuestPsf = useQueryParamValue('from_guest_psf');
	const {
		data: {
			petition,
			featureConfigs: { campaignActionsConfig },
		},
	} = usePsfSharePageData();
	const {
		data: { skipLinkText },
	} = usePageCopy();
	const [{ shared }] = usePsfSharePageState();
	const { translate } = useI18n();

	const skipUrl = useMemo(() => {
		if (campaignActionsConfig?.enabled) {
			return `/p/${encodeURIComponent(petition.slug)}/actions?source_location=${sourceLocation || 'combo_psf'}`;
		}

		return `/p/${encodeURIComponent(petition.slug)}/d${qs.stringify(
			{
				source_location: sourceLocation || 'combo_psf',
				...(['new', 'existing'].includes(guest || '') || fromGuestPsf === '1' ? { from_guest_psf: '1' } : undefined),
			},
			{ addQueryPrefix: true },
		)}`;
	}, [campaignActionsConfig?.enabled, petition.slug, sourceLocation, guest, fromGuestPsf]);

	const {
		data: { isFooterTapHandledInPage, isEnabled, isOpen, isPending, amountLocalized },
		actions: { handleFooterTap, handleOpenChange, handleChipIn, handleSkip },
	} = usePromoteSlideUp(skipUrl);

	return {
		data: {
			skipLabel: shared ? translate('corgi.psf.share.footer.continue') : skipLinkText,
			skipUrl,
			isFooterTapHandledInPage,
			promoteSlideUp: { isEnabled, isOpen, isPending, amountLocalized },
		},
		actions: {
			handleSkipClick: useCallback(() => {
				void handleFooterTap();
			}, [handleFooterTap]),
			handlePromoteSlideUpOpenChange: handleOpenChange,
			handlePromoteSlideUpChipIn: handleChipIn,
			handlePromoteSlideUpSkip: handleSkip,
		},
	};
}
